/**
 * shared/essentials-spec.ts
 *
 * SINGLE declarative source for the "Ten Essentials" / Estate Ready Score model.
 * Both the server ERS computation (`server/estate-ready-score.ts`) and the client
 * section-completion predicate (`client/src/lib/section-completion.ts`) read from
 * this module, so every atom of an essential's definition — label, tier, section
 * slug, the field(s) it reads, its threshold, its conditional gate, its deep-link —
 * lives in EXACTLY one place.
 *
 * Pure data + pure derivations. NO DB, NO React, no server-only or client-only
 * deps (mirrors `shared/readiness.ts`'s boundary discipline; only the type
 * `ReadinessTier` is imported, type-only, so there is no runtime import cycle).
 *
 * Behaviour contract: this module encodes the EXACT current behaviour of each
 * essential. The server and client engines historically differed in how they treat
 * seeded scaffold rows; that difference is named explicitly per check via
 * `completionMode` (Phase-0 trap #1) and each interpreter honours its own mode —
 * the spec deliberately does NOT unify them. Section tier (`generic_sections.priority`)
 * is a DIFFERENT axis from an essential's `tier` and is referenced, never merged
 * (trap #3). The care-plan gate is a DATA predicate (a Dependent-Overview row with a
 * name), NOT `clients.has_children` (trap #4). `legal-your-executor` reads
 * `executor_access`, flagged via `crossSource` (trap #5). `beneficiaries` is the
 * one registry-excluded section literal (trap #6).
 *
 * See `field-display-rules.md` §10 (the rulebook; `completion-model.md` retired s191)
 * and the Phase-0 findings for the full rationale.
 */
import { SECTION_SLUGS } from "./constants/sections";
import { CATEGORY_SLUGS } from "./constants/categories";
import { EXECUTOR_TERM } from "./constants/terminology";
import type { ReadinessTier } from "./readiness";

// `beneficiaries` is intentionally a plain literal, not a SECTION_SLUGS constant —
// the slug is a common word that collides with non-slug literals, so it is kept out
// of the constants registry (s77 diagnostic) and is NOT flagged by check:slugs.
export const BENEFICIARIES_SLUG = "beneficiaries";

const deepLink = (categorySlug: string, sectionSlug: string) =>
  `/category/${categorySlug}?section=${sectionSlug}`;

// ── About You identity fields (section-completion) ────────────────────────────
// These 7 About You card fields are the identity set. The `personal_identity` ERS
// *essential* was removed (s86) as redundant with About You section completion. The
// former ≥5-of-7 THRESHOLD was RETIRED s111 (D-110.3): About You now completes only
// when ALL required fields are filled-or-declined (uniform card rule — see
// field-display-rules.md §3; completion-model.md retired s191). This list survives — the tests build
// fixtures from it and it documents the canonical identity keys.
export const ABOUT_YOU_IDENTITY_FIELDS = [
  // FIELDS-NAME-1 (s135): name is captured as parts; first + family are the required identity keys
  // (full_legal_name is now DERIVED, not a required input).
  "first_name",
  "family_name",
  "date_of_birth",
  "place_of_birth",
  "marital_status",
  "home_address",
  "father_name",
  "mother_maiden_name",
] as const;

// ── Predicate kinds ───────────────────────────────────────────────────────────
// A closed set that reproduces every current ERS predicate exactly. The server
// interpreter dispatches on `kind`.
//
// 🔴 s191 (D-191.6) — READ THIS BEFORE INFERRING ANYTHING ABOUT RESOLUTION. `completionMode`
// documents the row-level seeded gating applied by `evalPredicate`, and since D-156.10
// `evalPredicate` feeds ONLY the partial-state COPY (`isPartialState` — the card subline and
// the router line). It does NOT decide whether an essential ticks. Resolution runs through
// `sectionBar`: an essential resolves iff its bound section is complete under the shared
// section math (ONE BAR). The gating described below and the section bar are separate paths
// and are not unified — but do not read "not unified" as "the gating below is what makes an
// essential done". It is not. The prior wording said exactly that and misled two readers.
export type CompletionMode =
  | "row-qualifying" // server: a row counts once it has ≥1 non-seeded entry
  | "card-all-present" // single-instance card (no rowId)

export type EssentialPredicate =
  // executor_access row exists (status != revoked); no generic-entries read.
  // hasEntry(slug, f) OR-ed across `fields` (phone access = password OR pin).
  | { kind: "any-field"; fields: readonly string[] }
  // a single row where the label field matches `op` AND the target field is filled
  // (will: doc_type == "Will & Testament" + location; life: type contains "life" +
  // provider; beneficiaries: name non-empty + share).
  | {
      kind: "row-match";
      labelKey: string;
      op: "equals" | "contains" | "nonempty" | "in";
      labelValue?: string;
      /** Used only with op "in" — the label value must be one of these exactly
       *  (s95: estate_lawyer accepts any legal-professional role). */
      labelValues?: readonly string[];
      targetKey: string;
    }
  // ≥ `min` distinct rows have `field` filled (gov-IDs ≥2; key-docs ≥3).
  | { kind: "count-rows"; field: string; min: number }
  // ≥ `min` of `fields` filled on the section (About You identity ≥5 of 7).
  | { kind: "identity-count"; fields: readonly string[]; min: number };

// ── Conditional (done-when-declined) ──────────────────────────────────────────
// The client situational flags an essential can be gated on (s87, D-87.2). Replaces
// the former data-predicate care-plan gate (trap #4 retired): Guardian + Property are
// now "done-when-declined" off the About You flags directly.
export type ConditionalFlag = "hasChildren" | "hasDependents" | "ownsProperty";

export interface EssentialConditional {
  // Done-when-declined semantics (resolved in the evaluator + buildEssentials):
  //   • EVERY flag === false  → the essential is DONE without any data (declined).
  //   • ANY flag === null/undefined → OUTSTANDING (prompt the planner to answer the
  //     About You question — never silently complete on an unanswered flag).
  //   • SOME flag === true    → evaluate the data predicate as normal.
  // A manually-N/A'd section still resolves the check regardless (explicit opt-out).
  flags: readonly ConditionalFlag[];
}

// ── One ERS check ─────────────────────────────────────────────────────────────
export interface EssentialCheckSpec {
  checkKey: string;
  sectionSlug: string; // the section the predicate reads (ignored for cross-source)
  predicate: EssentialPredicate;
  completionMode: CompletionMode;
  catA?: boolean; // routes through catAFieldPresent on the client (gov-ID number)
  // Flat 13-check ("items") view — advisor portal + nextSteps source.
  itemLabel: string;
  itemLink: string;
  // s159 (essentials ruling: behaviour as built, copy fixed). Shown on the planner
  // essentials card while the check's own data predicate PASSES but its bound section
  // is still incomplete under the D-156 bar — i.e. the headline fact is recorded and
  // the remaining work is the rest of the section. Without it the row reads as though
  // nothing had been done. Copy only: it changes no status, score, or link.
  partialSubline?: string;
  // The ROUTER line for that same partial state, replacing `metaLabel` in `nextSteps`.
  // Same trigger and same two suppressions as `partialSubline` — one predicate drives
  // both, so the card and the router can never disagree about what is left to do.
  // (Without it the router kept asking for the headline fact the planner had already
  // recorded — the contradiction the s159 staging pack surfaced.)
  partialMetaLabel?: string;
  // Router / "Start Here · Next" view.
  metaLabel: string;
  tier: ReadinessTier;
  deepLink: string;
  // Done-when-declined off a client flag (Guardian, Property — s87). Mutually
  // exclusive in practice with `crossSource`.
  conditional?: EssentialConditional;
}

// The 13 checks in CANONICAL CARD ORDER (s87, D-87.1) — the order the essentials card is
// rendered, flattening the 10 ESSENTIAL_GROUPS' sub-checks in display order. This array's
// index IS `CHECK_ROUTER_INDEX`.
//
// 🔴 s191 (D-191.3) — the NEXT UP walk no longer follows this array alone. It sorts by TIER
// first (T1 before T2) and uses this index only as the within-tier tiebreak, enforcing D-78.7.
// Funeral wishes is T1 but sits LAST here per the locked s87 order — so it renders last on the
// CARD while surfacing among the T1s in the WALK. Both orders are deliberate and they differ.
// (Count corrected 14 -> 13 under D-191.6: "Documents & IDs" left the essentials in s111
// Build 6 — two checks out, `protect_access` one in.)
export const ESSENTIAL_CHECKS: readonly EssentialCheckSpec[] = [
  // 1 · Protect Your Access (s111 Build 6) — the keys that unlock everything else.
  //     Resolves on the 3 NON-Cat-A fields only (Build 6 Q1b): the 2 Cat A fields
  //     (phone_unlock, primary_email_password) are required for the SECTION green-check but
  //     EXCLUDED from the essential, so it stays server-completable under SDE (Cat A values
  //     never reach the server + can't be declined — the same Item-21 gap government_ids
  //     had, sidestepped here by gating the essential on the 3 server-visible fields).
  {
    checkKey: "protect_access",
    sectionSlug: SECTION_SLUGS.PROTECT_YOUR_ACCESS,
    predicate: { kind: "identity-count", fields: ["phone_make_model", "primary_email", "secure_file_location"], min: 3 }, // FIELD-AUDIT-1 #13 (D-154.5): phone_location → phone_make_model
    completionMode: "card-all-present",
    itemLabel: "Access keys recorded",
    itemLink: "/category/protect-your-access",
    metaLabel: "Record your phone, email, and secure-file access",
    tier: "T1",
    deepLink: deepLink(CATEGORY_SLUGS.PROTECT_YOUR_ACCESS, SECTION_SLUGS.PROTECT_YOUR_ACCESS),
  },
  // 2 · Your Will & executor (will_location + executor_identified)
  {
    checkKey: "will_location",
    sectionSlug: SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS,
    predicate: { kind: "row-match", labelKey: "doc_type", op: "equals", labelValue: "Will & Testament", targetKey: "location" },
    completionMode: "row-qualifying",
    itemLabel: "Will recorded",
    itemLink: "/category/legal",
    metaLabel: "Record your Will and where it's kept",
    tier: "T1",
    deepLink: deepLink(CATEGORY_SLUGS.LEGAL, SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS),
  },
  {
    // s87 (F86-5): executor person data is now canonical in generic_entries
    // (legal-your-executor, expandable). "Named" = ≥1 user-touched row with `name`
    // filled — the standard row-qualifying any-field check (was a cross-source read of
    // executor_access). The executor_access auth row is kept in sync by the upsert hook.
    checkKey: "executor_identified",
    sectionSlug: SECTION_SLUGS.LEGAL_YOUR_EXECUTOR,
    predicate: { kind: "any-field", fields: ["name"] },
    completionMode: "row-qualifying",
    itemLabel: "Executor named",
    itemLink: "/category/legal",
    metaLabel: "Name your executor",
    tier: "T1",
    deepLink: deepLink(CATEGORY_SLUGS.LEGAL, SECTION_SLUGS.LEGAL_YOUR_EXECUTOR),
  },
  // 2 · Estate lawyer & key advisors
  {
    checkKey: "estate_lawyer",
    sectionSlug: SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS,
    // s95 completion-audit C1: broadened from role == "Estate Lawyer" to any
    // legal-professional role so a planner whose legal advisor is a Notary still
    // resolves. The other roles (accountant, financial advisor, etc.) are not legal.
    predicate: { kind: "row-match", labelKey: "role", op: "in", labelValues: ["Estate Lawyer", "Notary"], targetKey: "name" },
    completionMode: "row-qualifying",
    itemLabel: "Estate lawyer added",
    itemLink: "/category/legal",
    metaLabel: "Add your estate lawyer",
    tier: "T1",
    deepLink: deepLink(CATEGORY_SLUGS.LEGAL, SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS),
  },
  // 3 · Beneficiaries
  {
    checkKey: "beneficiaries",
    sectionSlug: BENEFICIARIES_SLUG,
    // s94 (Stephanie F4): beneficiary_share deactivated → resolve on a named beneficiary alone.
    predicate: { kind: "any-field", fields: ["beneficiary_surname"] }, // BENEFICIARY-FILL-1 (s156): name split; surname is the always-present field (charity name lives here too)
    completionMode: "row-qualifying",
    itemLabel: "Beneficiaries added",
    itemLink: "/category/legal",
    metaLabel: "Add your beneficiaries",
    tier: "T1",
    deepLink: deepLink(CATEGORY_SLUGS.LEGAL, BENEFICIARIES_SLUG),
  },
  // 4 · Your finances (primary_bank + investments_documented + income_pensions)
  {
    checkKey: "primary_bank",
    sectionSlug: SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS,
    predicate: { kind: "any-field", fields: ["bank"] },
    completionMode: "row-qualifying",
    itemLabel: "Bank account added",
    itemLink: "/category/financial-and-insurance",
    metaLabel: "Add a bank account",
    tier: "T2",
    deepLink: deepLink(CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE, SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS),
  },
  {
    // Investments sub of Your finances. Keys on `institution` — the executor needs to
    // know WHERE the investment is held. NOT `market_value` (goes stale) and NOT
    // `inv_type` (pre-filled on seeded rows → scaffold). row-qualifying excludes them.
    checkKey: "investments_documented",
    sectionSlug: SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP,
    predicate: { kind: "any-field", fields: ["institution"] },
    completionMode: "row-qualifying",
    itemLabel: "Investment account recorded",
    itemLink: "/category/financial-and-insurance",
    metaLabel: "Record an investment account",
    tier: "T2",
    deepLink: deepLink(CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE, SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP),
  },
  {
    // Income & pensions sub of Your finances (s87). Keys on `source` — the recurring
    // income's payer (CPP, a pension, royalties…). `source` is pre-filled on every
    // seeded scaffold row, so row-qualifying gating excludes untouched scaffolds
    // (mirrors `primary_bank` / investments, D-83.2).
    checkKey: "income_pensions",
    sectionSlug: SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS,
    predicate: { kind: "any-field", fields: ["source"] },
    completionMode: "row-qualifying",
    itemLabel: "Income source recorded",
    itemLink: "/category/financial-and-insurance",
    metaLabel: "Record an income source",
    tier: "T2",
    deepLink: deepLink(CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE, SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS),
  },
  // 5 · Insurance — life
  {
    checkKey: "life_insurance",
    sectionSlug: SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES,
    predicate: { kind: "row-match", labelKey: "type", op: "contains", labelValue: "life", targetKey: "provider" },
    completionMode: "row-qualifying",
    itemLabel: "Life insurance added",
    itemLink: "/category/financial-and-insurance",
    metaLabel: "Add a life insurance policy",
    tier: "T2",
    deepLink: deepLink(CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE, SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES),
  },
  // 6 · Debts & liabilities
  {
    // The real uncovered executor-liability gap (s86). Requires BOTH a creditor name
    // AND a balance on the SAME row: a balance with no creditor is useless, and 2 of 6
    // seeded rows pre-fill `creditor` — requiring `balance` (empty in every seed)
    // avoids counting that scaffold.
    checkKey: "debts",
    sectionSlug: SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES,
    predicate: { kind: "row-match", labelKey: "creditor", op: "nonempty", targetKey: "balance" },
    completionMode: "row-qualifying",
    itemLabel: "Debts & liabilities recorded",
    itemLink: "/category/financial-and-insurance",
    metaLabel: "Record your debts & liabilities",
    tier: "T2",
    deepLink: deepLink(CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE, SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES),
  },
  // s111 Build 6: the "Documents & IDs" essential (government_ids + key_documents) was
  // REMOVED — replaced at position 1 by "Protect Your Access". Government IDs and Where-
  // Are-They remain normal sections (their completion still shows in category progress),
  // just no longer among the 10 essentials. Denominator stays 10.
  // 8 · Guardian for your children (conditional — done-when-declined off the single
  //     About You children/dependents flag; s108 DB-S5 unified C&D). Resolves on a
  //     dependent row that has guardianship info AND a named primary guardian — the
  //     old guardianship sibling section is retired; it now reads the unified Overview.
  {
    checkKey: "guardianship_plan",
    sectionSlug: SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW,
    predicate: { kind: "row-match", labelKey: "has_guardianship_info", op: "equals", labelValue: "true", targetKey: "primary_guardian" },
    completionMode: "row-qualifying",
    // s159: retitled "Guardian named" → "Guardianship plan". Under the D-156 bar this
    // check ticks on the WHOLE Children & Dependents Overview section, not on the guardian
    // name alone, so the old label promised a smaller job than the row actually holds open.
    itemLabel: "Guardianship plan",
    itemLink: "/category/children-and-dependents",
    metaLabel: "Name a guardian for your children",
    // The middle state the retitle exposes: guardian recorded, section still open.
    partialSubline: `Guardian named — now complete each child's details so your ${EXECUTOR_TERM} has everything`,
    partialMetaLabel: "Complete each child's details",
    tier: "T2",
    deepLink: deepLink(CATEGORY_SLUGS.CHILDREN_AND_DEPENDENTS, SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW),
    conditional: { flags: ["hasChildren"] },
  },
  // 9 · Property (conditional — done-when-declined off owns_property; s87)
  {
    // s91 (item 15): keys on `address`, NOT `prop_type`. Every default property row is
    // seeded with a `prop_type` ("Primary Residence", etc.), so under row-qualifying
    // gating the essential completed the moment ANY field on a seeded row was touched —
    // a premature checkmark. `address` is never pre-seeded, so it signals a genuinely
    // recorded property (same reasoning as investments keying on `institution`, not the
    // pre-filled `inv_type`).
    checkKey: "property_secured",
    sectionSlug: SECTION_SLUGS.ASSETS_PROPERTIES,
    predicate: { kind: "any-field", fields: ["address"] },
    completionMode: "row-qualifying",
    itemLabel: "Property recorded",
    itemLink: "/category/assets",
    metaLabel: "Record a property you own",
    tier: "T2",
    deepLink: deepLink(CATEGORY_SLUGS.ASSETS, SECTION_SLUGS.ASSETS_PROPERTIES),
    conditional: { flags: ["ownsProperty"] },
  },
  // 10 · Funeral wishes
  {
    checkKey: "funeral_wishes",
    sectionSlug: SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES,
    predicate: { kind: "any-field", fields: ["wishes"] },
    completionMode: "row-qualifying",
    itemLabel: "Funeral wishes noted",
    itemLink: "/category/instructions-for-executors",
    metaLabel: "Note your funeral wishes",
    tier: "T1",
    deepLink: deepLink(CATEGORY_SLUGS.MY_WISHES_AND_LEGACY, SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES),
  },
];

export const CHECK_BY_KEY: ReadonlyMap<string, EssentialCheckSpec> = new Map(
  ESSENTIAL_CHECKS.map((c) => [c.checkKey, c]),
);

// Router sort order (former META_ORDER): index into ESSENTIAL_CHECKS.
export const CHECK_ROUTER_INDEX: ReadonlyMap<string, number> = new Map(
  ESSENTIAL_CHECKS.map((c, i) => [c.checkKey, i]),
);

// The flat 13-check ("items") order — DERIVED from ESSENTIAL_CHECKS so it can never
// drift from the spec/card order (s87). All checks are always emitted now (no
// gated-off omission); conditional checks resolve via done-when-declined.
// (Count corrected 14 -> 13 under D-191.6; see the ESSENTIAL_CHECKS header.)
export const ITEM_ORDER: readonly string[] = ESSENTIAL_CHECKS.map((c) => c.checkKey);

// ── The 10 Essentials grouping (session 79 → s87 overhaul) ─────────────────────
// The 14 checks grouped into 10 headline essentials, in FIXED planner-card display
// order (s87 locked order, D-87.1). Multi-check essentials ("Your Will & executor",
// "Your finances", "Documents & IDs") are "done" only when ALL their subs are
// RESOLVED (data present, section manually N/A'd, or — for conditionals — declined).
// Guardian + Property are conditional (done-when-declined); the denominator is now
// ALWAYS 10 (no essential is ever excluded — s87, D-87.2).
export interface EssentialGroupSpec {
  key: string;
  label: string;
  tier: ReadinessTier;
  checkKeys: readonly string[];
}

export const ESSENTIAL_GROUPS: readonly EssentialGroupSpec[] = [
  // s111 Build 6 — new essential #1, replacing "Documents & IDs" (denominator stays 10).
  { key: "protect_access", label: "Protect Your Access", tier: "T1", checkKeys: ["protect_access"] },
  { key: "will", label: "Your Will & executor", tier: "T1", checkKeys: ["will_location", "executor_identified"] },
  { key: "lawyer", label: "Estate lawyer & key advisors", tier: "T1", checkKeys: ["estate_lawyer"] },
  { key: "beneficiaries", label: "Beneficiaries", tier: "T1", checkKeys: ["beneficiaries"] },
  { key: "finances", label: "Your finances", tier: "T2", checkKeys: ["primary_bank", "investments_documented", "income_pensions"] },
  { key: "life_insurance", label: "Insurance — life", tier: "T2", checkKeys: ["life_insurance"] },
  { key: "debts", label: "Debts & liabilities", tier: "T2", checkKeys: ["debts"] },
  { key: "guardian", label: "Guardian for your children", tier: "T2", checkKeys: ["guardianship_plan"] },
  { key: "property", label: "Property", tier: "T2", checkKeys: ["property_secured"] },
  { key: "funeral", label: "Funeral wishes", tier: "T1", checkKeys: ["funeral_wishes"] },
];

// ── Derivations ───────────────────────────────────────────────────────────────

// The section slugs targeted by the 10 ESSENTIAL checks, derived from the grouping →
// each check's deepLink. Source of truth for the accordion-row "ESSENTIAL" badge set
// (re-exported as `ESSENTIAL_SECTION_SLUGS` from shared/readiness.ts). Checks whose
// deep-link has no `?section=` surface (key_documents → Where Is Everything) are
// excluded — they have no accordion row / badge slot.
export const ESSENTIAL_SECTION_SLUGS: ReadonlySet<string> = new Set(
  ESSENTIAL_GROUPS
    .flatMap((g) => g.checkKeys)
    .map((k) => CHECK_BY_KEY.get(k)?.deepLink ?? "")
    .map((link) => link.match(/[?&]section=([^&]+)/)?.[1])
    .filter((s): s is string => !!s),
);

// Forward-looking disambiguation of the overloaded `isRequired` flag (Phase-0
// trap: `isRequired` marks BOTH readiness stars AND ordinary table-primary
// requireds). DERIVED from the spec predicates (single source) rather than a
// second per-field marker on template-spec — so there is nothing to keep in sync.
// Maps each essential section slug → the set of value field keys its predicate
// reads. NOT wired into runtime completion yet (no behaviour change); exposed for
// the future identity-swap + star audits.
export const ESSENTIAL_STAR_FIELDS: ReadonlyMap<string, ReadonlySet<string>> = (() => {
  const m = new Map<string, Set<string>>();
  const add = (slug: string, key: string) => {
    if (!m.has(slug)) m.set(slug, new Set());
    m.get(slug)!.add(key);
  };
  for (const c of ESSENTIAL_CHECKS) {
    const p = c.predicate;
    switch (p.kind) {
      case "any-field":
        p.fields.forEach((f) => add(c.sectionSlug, f));
        break;
      case "row-match":
        add(c.sectionSlug, p.labelKey);
        add(c.sectionSlug, p.targetKey);
        break;
      case "count-rows":
        add(c.sectionSlug, p.field);
        break;
      case "identity-count":
        p.fields.forEach((f) => add(c.sectionSlug, f));
        break;
    }
  }
  return m;
})();

/** True iff `fieldKey` in `sectionSlug` is a value field read by an essential
 *  predicate — i.e. a readiness "star", as opposed to an ordinary table-primary
 *  `isRequired`. Derived from the spec; safe to call for any section. */
export function isEssentialStarField(sectionSlug: string, fieldKey: string): boolean {
  return ESSENTIAL_STAR_FIELDS.get(sectionSlug)?.has(fieldKey) ?? false;
}

// ── Essential-tied scaffold rows (s99, D-99.4 two-zone split) ──────────────────
// A scaffold row is "essential-tied" when an essential predicate pins a SPECIFIC
// row of the section by a label value — e.g. Insurance Policies' life_insurance
// check (`type` CONTAINS "life") pins the "Life Insurance" scaffold, and the will
// check (`doc_type` EQUALS "Will & Testament") pins the Will row. Deleting such a
// scaffold would orphan the essential, so it stays in the expanded section's TOP
// zone even when empty. `any-field` / `count-rows` / `nonempty` predicates pin no
// specific row (any row satisfies them) → they contribute NO ties, so their
// scaffolds all fall to the suggestion tray.

export interface EssentialRowTie {
  /** The label field whose value identifies the pinned row (e.g. `type`, `doc_type`). */
  labelKey: string;
  op: "equals" | "contains" | "in";
  /** Normalised: equals/contains → `[labelValue]`; in → `labelValues`. */
  labelValues: readonly string[];
}

/** The row-match ties an essential places on `sectionSlug` (empty when none). */
export function essentialRowTies(sectionSlug: string): EssentialRowTie[] {
  const ties: EssentialRowTie[] = [];
  for (const c of ESSENTIAL_CHECKS) {
    if (c.sectionSlug !== sectionSlug) continue;
    const p = c.predicate;
    if (p.kind !== "row-match") continue;
    if ((p.op === "equals" || p.op === "contains") && p.labelValue != null) {
      ties.push({ labelKey: p.labelKey, op: p.op, labelValues: [p.labelValue] });
    } else if (p.op === "in" && p.labelValues && p.labelValues.length > 0) {
      ties.push({ labelKey: p.labelKey, op: "in", labelValues: p.labelValues });
    }
    // op "nonempty" pins no value → not a tie.
  }
  return ties;
}

/** Does the section have ANY essential-tied scaffold (i.e. an essential pins a
 *  specific row by label value)? When false, every scaffold is freely
 *  dismissible / belongs in the suggestion tray. */
export function sectionHasEssentialRowTie(sectionSlug: string): boolean {
  return essentialRowTies(sectionSlug).length > 0;
}

/** Is a row essential-tied? `getLabelValue(fieldKey)` returns the row's value for
 *  a label field. Case-insensitive throughout; `contains` mirrors the ERS
 *  `type contains "life"` semantics. A row with no matching label value is not
 *  tied (so an empty/blank scaffold is tied only if its label cell still carries
 *  the pinned value — which seeded scaffolds do, e.g. type="Life Insurance"). */
export function isEssentialTiedRow(
  sectionSlug: string,
  getLabelValue: (fieldKey: string) => string | null | undefined,
): boolean {
  for (const tie of essentialRowTies(sectionSlug)) {
    const raw = (getLabelValue(tie.labelKey) ?? "").trim().toLowerCase();
    if (!raw) continue;
    const matched = tie.labelValues.some((lv) => {
      const t = lv.toLowerCase();
      return tie.op === "contains" ? raw.includes(t) : raw === t;
    });
    if (matched) return true;
  }
  return false;
}
